import {
  escapeHtml,
  formatDimensionsPdf,
  formatNetWeightPdf,
  formatPricePdf,
  getProductImageSrc,
  packLinesHtml,
  pdfCategoryRaw,
  pdfColorRaw,
  pdfFinishRaw,
  pdfHsnRaw,
  pdfMaterialRaw,
  pdfNameRaw,
  pdfSkuRaw,
} from './cartUtils';

export const PDF_EXPORT_CURRENCIES = [
  { code: 'INR', symbol: '₹', label: 'INR (₹)' },
  { code: 'USD', symbol: '$', label: 'USD ($)' },
  { code: 'EUR', symbol: '€', label: 'EUR (€)' },
  { code: 'GBP', symbol: '£', label: 'GBP (£)' },
];

const COLLECTION_PDF_PRINT_STYLES = `
  @page { size: landscape; margin: 14mm 16mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, Helvetica, sans-serif;
    color: #111;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pdf-page {
    min-height: calc(100vh - 28mm);
    display: flex;
    flex-direction: column;
    padding-bottom: 8mm;
  }
  .pdf-top-bar {
    height: 36px;
    background: #4a4e8f;
    display: flex;
    align-items: center;
    padding: 0 12px;
    margin-bottom: 14px;
  }
  .pdf-page-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    background: #14b8a6;
    color: #111;
    font-weight: 700;
    font-size: 14px;
    border: 2px solid #0d9488;
  }
  .pdf-body {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
    flex: 1;
  }
  .pdf-image-cell {
    flex: 0 0 38%;
    max-width: 38%;
    min-height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
  }
  .pdf-product-img {
    max-width: 100%;
    max-height: 280px;
    object-fit: contain;
  }
  .pdf-no-image {
    width: 100%;
    min-height: 180px;
    background: #f1f3f8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #888;
    font-size: 13px;
    border-radius: 4px;
  }
  .pdf-spec-cell {
    flex: 1;
    min-width: 0;
  }
  .pdf-spec-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11px;
  }
  .pdf-spec-table tr:nth-child(even) td {
    background: #f5f6f8;
  }
  .pdf-spec-table td {
    padding: 9px 12px;
    border: 1px solid #dcdce4;
    vertical-align: top;
  }
  .pdf-label {
    font-weight: 700;
    text-transform: uppercase;
    width: 34%;
    color: #222;
    letter-spacing: 0.02em;
  }
  .pdf-val {
    color: #333;
  }
  .pdf-multiline {
    line-height: 1.45;
  }
  .pdf-company-footer {
    margin-top: auto;
    padding-top: 12px;
    font-size: 11px;
    color: #222;
  }
  @media print {
    body { margin: 0; }
    .pdf-page { min-height: auto; }
  }
`;

/**
 * Build full HTML document for print/PDF (landscape, one page per product).
 */
export function buildCollectionPdfHtmlDocument(
  collectionName,
  enrichedProducts,
  currencySymbol,
  currencyCode,
  selectedFields = ['SKU#', 'DESCRIPTION', 'CATEGORY', 'DIMENSIONS'],
) {
  const hasField = (field) => Array.isArray(selectedFields) && selectedFields.includes(field);
  let pagesHtml = '';

  enrichedProducts.forEach((product, index) => {
    const pageNum = index + 1;
    const isLast = index === enrichedProducts.length - 1;
    const imageSrcRaw =
      getProductImageSrc(product) || product.mainImage || null;
    const imageSrc = imageSrcRaw ? escapeHtml(imageSrcRaw) : '';

    const sku = escapeHtml(pdfSkuRaw(product) || '—');
    const prodName = escapeHtml(pdfNameRaw(product) || '—');
    const dims = escapeHtml(formatDimensionsPdf(product));
    const netWt = escapeHtml(formatNetWeightPdf(product));
    const price = escapeHtml(formatPricePdf(product, currencySymbol));
    const hsn = escapeHtml(pdfHsnRaw(product) || '—');
    const category = escapeHtml(pdfCategoryRaw(product) || '—');
    const finish = escapeHtml(pdfFinishRaw(product) || '—');
    const material = escapeHtml(pdfMaterialRaw(product) || '—');
    const color = escapeHtml(pdfColorRaw(product) || '—');
    const moq =
      product.moq != null && product.moq !== ''
        ? escapeHtml(String(product.moq))
        : '—';
    const innerPack = packLinesHtml(product.innerPacks ?? product.inner_packs);
    const masterPack = packLinesHtml(product.masterPacks ?? product.master_packs);
    const remark = escapeHtml(
      (product.remark != null && String(product.remark).trim() !== ''
        ? String(product.remark).trim()
        : null) || '—',
    );

    const breakStyle = isLast ? '' : 'page-break-after: always;';

    pagesHtml += `
          <div class="pdf-page" style="${breakStyle}">
            <header class="pdf-top-bar">
              <span class="pdf-page-badge" aria-hidden="true">${pageNum}</span>
            </header>
            <div class="pdf-body">
              <div class="pdf-image-cell">
                ${
                  imageSrc
                    ? `<img class="pdf-product-img" src="${imageSrc}" alt="" />`
                    : `<div class="pdf-no-image">No image</div>`
                }
              </div>
              <div class="pdf-spec-cell">
                <table class="pdf-spec-table">
                  <tbody>
                    ${hasField('SKU#') ? `<tr><td class="pdf-label">SKU#</td><td class="pdf-val">${sku}</td></tr>` : ''}
                    ${hasField('DESCRIPTION') ? `<tr><td class="pdf-label">DESCRIPTION</td><td class="pdf-val">${prodName}</td></tr>` : ''}
                    ${hasField('DIMENSIONS') ? `<tr><td class="pdf-label">DIMENSIONS</td><td class="pdf-val">${dims}</td></tr>` : ''}
                    ${hasField('FINISH') ? `<tr><td class="pdf-label">FINISH</td><td class="pdf-val">${finish}</td></tr>` : ''}
                    ${hasField('MATERIAL') ? `<tr><td class="pdf-label">MATERIAL</td><td class="pdf-val">${material}</td></tr>` : ''}
                    ${hasField('COLOR') ? `<tr><td class="pdf-label">COLOR</td><td class="pdf-val">${color}</td></tr>` : ''}
                    ${hasField('NET WEIGHT') ? `<tr><td class="pdf-label">NET WEIGHT</td><td class="pdf-val">${netWt}</td></tr>` : ''}
                    ${hasField('PRICE') ? `<tr><td class="pdf-label">PRICE (${currencyCode})</td><td class="pdf-val">${price}</td></tr>` : ''}
                    ${hasField('HSN') ? `<tr><td class="pdf-label">HSN</td><td class="pdf-val">${hsn}</td></tr>` : ''}
                    ${hasField('CATEGORY') ? `<tr><td class="pdf-label">CATEGORY</td><td class="pdf-val">${category}</td></tr>` : ''}
                    ${hasField('MOQ') ? `<tr><td class="pdf-label">MOQ</td><td class="pdf-val">${moq}</td></tr>` : ''}
                    ${hasField('INNER PACK') ? `<tr><td class="pdf-label">INNER PACK</td><td class="pdf-val pdf-multiline">${innerPack}</td></tr>` : ''}
                    ${hasField('MASTER PACK') ? `<tr><td class="pdf-label">MASTER PACK</td><td class="pdf-val pdf-multiline">${masterPack}</td></tr>` : ''}
                    ${hasField('REMARKS') ? `<tr><td class="pdf-label">REMARKS</td><td class="pdf-val">${remark}</td></tr>` : ''}
                  </tbody>
                </table>
              </div>
            </div>
            <footer class="pdf-company-footer">aCube Kraft Pvt. Ltd.</footer>
          </div>`;
  });

  return `<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${escapeHtml(collectionName)} — PDF</title>
            <style>
              ${COLLECTION_PDF_PRINT_STYLES}
            </style>
          </head>
          <body>${pagesHtml}</body>
        </html>`;
}

/** Opens print dialog with HTML content (expects pop-up allowed). */
export function openPrintWindowForHtml(htmlContent) {
  const printWindow = window.open('', '_blank', 'width=1100,height=800');
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 300);
  return true;
}
